/**
 * components/LayoutInspector.js - UI for editing section layout settings
 * Location: /app/frontend/src/admin/page_builder/components/
 * 
 * Inspector panel for selecting layout presets and configuring backgrounds.
 * Used in the Page Builder right panel when a section is selected.
 * 
 * NEW: Supports template constraints - disables invalid options based on
 * allowedLayouts, allowedBackgrounds, allowedCardVariants.
 */

import React, { useState, useMemo } from 'react';
import { 
  LayoutPreset, 
  LayoutPresetLabel, 
  getLayoutPresetOptions,
} from '../layout/presets';
import {
  BackgroundType,
  GradientDirection,
  BACKGROUND_PRESETS,
} from '../layout/backgrounds';
import CardVariantSelector from './CardVariantSelector';
import { CardVariant } from '../cards/variants';
import { getTemplateConstraints } from '../constraints';
import { 
  Square, 
  Columns, 
  PanelLeft, 
  PanelRight, 
  Image, 
  Layers,
  ChevronDown,
  Palette,
  Droplet,
  ImageIcon,
  CreditCard,
  Lock,
} from 'lucide-react';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Slider } from '../../../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';

// Icon mapping for layout presets
const LAYOUT_ICONS = {
  [LayoutPreset.SINGLE]: Square,
  [LayoutPreset.SPLIT_50_50]: Columns,
  [LayoutPreset.SPLIT_60_40]: PanelLeft,
  [LayoutPreset.SPLIT_40_60]: PanelRight,
  [LayoutPreset.MEDIA_LEFT]: Image,
  [LayoutPreset.MEDIA_RIGHT]: Image,
  [LayoutPreset.OVERLAY]: Layers,
};

/**
 * LayoutPresetSelector - Visual selector for layout presets
 * NEW: Supports constraints - disables layouts not in allowedLayouts
 */
export const LayoutPresetSelector = ({ value, onChange, allowedLayouts = null }) => {
  const options = getLayoutPresetOptions();
  
  // If no constraints, all layouts are allowed
  const isAllowed = (layoutValue) => {
    if (!allowedLayouts || allowedLayouts.length === 0) return true;
    return allowedLayouts.includes(layoutValue);
  };
  
  return (
    <div className="space-y-2">
      <Label className="text-zinc-300 text-sm">Layout Preset</Label>
      <div className="grid grid-cols-4 gap-2">
        <TooltipProvider>
          {options.map((option) => {
            const IconComponent = LAYOUT_ICONS[option.value] || Square;
            const isSelected = value === option.value;
            const allowed = isAllowed(option.value);
            
            const button = (
              <button
                key={option.value}
                onClick={() => allowed && onChange(option.value)}
                disabled={!allowed}
                className={`
                  p-3 rounded-lg border transition-all flex flex-col items-center gap-1 relative
                  ${!allowed 
                    ? 'border-zinc-800 bg-zinc-900/30 text-zinc-600 cursor-not-allowed opacity-50' 
                    : isSelected 
                      ? 'border-orange-500 bg-orange-500/20 text-orange-400' 
                      : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                  }
                `}
                title={allowed ? option.description : 'Not allowed for this section type'}
              >
                <IconComponent className="w-5 h-5" />
                <span className="text-[10px] font-medium truncate w-full text-center">
                  {option.label.split(' ')[0]}
                </span>
                {!allowed && (
                  <Lock className="w-3 h-3 absolute top-1 right-1 text-zinc-600" />
                )}
              </button>
            );
            
            if (!allowed) {
              return (
                <Tooltip key={option.value}>
                  <TooltipTrigger asChild>{button}</TooltipTrigger>
                  <TooltipContent side="top" className="bg-zinc-800 text-zinc-200 text-xs">
                    Not allowed for this section type
                  </TooltipContent>
                </Tooltip>
              );
            }
            
            return button;
          })}
        </TooltipProvider>
      </div>
      <p className="text-xs text-zinc-500 mt-1">
        {options.find(o => o.value === value)?.description}
      </p>
    </div>
  );
};

/**
 * BackgroundTypeSelector - Select background type
 * NEW: Supports constraints - disables backgrounds not in allowedBackgrounds
 */
export const BackgroundTypeSelector = ({ value, onChange, allowedBackgrounds = null }) => {
  // If no constraints, all backgrounds are allowed
  const isAllowed = (bgType) => {
    if (!allowedBackgrounds || allowedBackgrounds.length === 0) return true;
    return allowedBackgrounds.includes(bgType);
  };
  
  const backgroundOptions = [
    { value: BackgroundType.NONE, label: 'None', icon: Square },
    { value: BackgroundType.SOLID, label: 'Solid Color', icon: Palette },
    { value: BackgroundType.GRADIENT, label: 'Gradient', icon: Droplet },
    { value: BackgroundType.IMAGE, label: 'Image', icon: ImageIcon },
  ];
  
  return (
    <div className="space-y-2">
      <Label className="text-zinc-300 text-sm">Background Type</Label>
      <Select value={value || BackgroundType.NONE} onValueChange={onChange}>
        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          {backgroundOptions.map((option) => {
            const allowed = isAllowed(option.value);
            const Icon = option.icon;
            
            return (
              <SelectItem 
                key={option.value} 
                value={option.value} 
                disabled={!allowed}
                className={`text-white ${!allowed ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  {option.label}
                  {!allowed && <Lock className="w-3 h-3 ml-1 text-zinc-500" />}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {allowedBackgrounds && allowedBackgrounds.length < 4 && (
        <p className="text-[10px] text-zinc-600">
          Some backgrounds are restricted for this section type
        </p>
      )}
    </div>
  );
};

/**
 * SolidBackgroundEditor - Edit solid color background
 */
export const SolidBackgroundEditor = ({ config, onChange }) => {
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-zinc-300 text-sm">Color</Label>
        <div className="flex gap-2">
          <input
            type="color"
            value={config?.color || '#000000'}
            onChange={(e) => onChange({ ...config, color: e.target.value })}
            className="w-10 h-10 rounded border border-zinc-700 bg-zinc-800 cursor-pointer"
          />
          <Input
            value={config?.color || '#000000'}
            onChange={(e) => onChange({ ...config, color: e.target.value })}
            placeholder="#000000"
            className="bg-zinc-800 border-zinc-700 text-white flex-1"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * GradientBackgroundEditor - Edit gradient background
 */
export const GradientBackgroundEditor = ({ config, onChange }) => {
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-zinc-300 text-sm">Direction</Label>
        <Select 
          value={config?.direction || GradientDirection.TO_BOTTOM} 
          onValueChange={(val) => onChange({ ...config, direction: val })}
        >
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            {Object.entries(GradientDirection).map(([key, value]) => (
              <SelectItem key={key} value={value} className="text-white capitalize">
                {key.toLowerCase().replace(/_/g, ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label className="text-zinc-300 text-sm">Start Color</Label>
        <div className="flex gap-2">
          <input
            type="color"
            value={config?.from || '#18181b'}
            onChange={(e) => onChange({ ...config, from: e.target.value })}
            className="w-10 h-10 rounded border border-zinc-700 bg-zinc-800 cursor-pointer"
          />
          <Input
            value={config?.from || '#18181b'}
            onChange={(e) => onChange({ ...config, from: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white flex-1"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label className="text-zinc-300 text-sm">End Color</Label>
        <div className="flex gap-2">
          <input
            type="color"
            value={config?.to || '#000000'}
            onChange={(e) => onChange({ ...config, to: e.target.value })}
            className="w-10 h-10 rounded border border-zinc-700 bg-zinc-800 cursor-pointer"
          />
          <Input
            value={config?.to || '#000000'}
            onChange={(e) => onChange({ ...config, to: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white flex-1"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * ImageBackgroundEditor - Edit image background
 */
export const ImageBackgroundEditor = ({ config, onChange }) => {
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-zinc-300 text-sm">Image URL</Label>
        <Input
          value={config?.url || ''}
          onChange={(e) => onChange({ ...config, url: e.target.value })}
          placeholder="https://..."
          className="bg-zinc-800 border-zinc-700 text-white"
        />
      </div>
      
      {config?.url && (
        <div className="aspect-video rounded-lg overflow-hidden border border-zinc-700">
          <img 
            src={config.url} 
            alt="Background preview" 
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      <div className="space-y-2">
        <Label className="text-zinc-300 text-sm">Position</Label>
        <Select 
          value={config?.position || 'center center'} 
          onValueChange={(val) => onChange({ ...config, position: val })}
        >
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="center center" className="text-white">Center</SelectItem>
            <SelectItem value="center top" className="text-white">Top</SelectItem>
            <SelectItem value="center bottom" className="text-white">Bottom</SelectItem>
            <SelectItem value="left center" className="text-white">Left</SelectItem>
            <SelectItem value="right center" className="text-white">Right</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label className="text-zinc-300 text-sm">Size</Label>
        <Select 
          value={config?.size || 'cover'} 
          onValueChange={(val) => onChange({ ...config, size: val })}
        >
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="cover" className="text-white">Cover</SelectItem>
            <SelectItem value="contain" className="text-white">Contain</SelectItem>
            <SelectItem value="auto" className="text-white">Auto</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

/**
 * OverlayEditor - Edit overlay opacity
 */
export const OverlayEditor = ({ value, onChange }) => {
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300 text-sm">Overlay Opacity</Label>
          <span className="text-xs text-zinc-500 font-mono">{value || 0}%</span>
        </div>
        <Slider
          value={[value || 0]}
          onValueChange={([val]) => onChange(val)}
          min={0}
          max={100}
          step={5}
          className="w-full"
        />
      </div>
    </div>
  );
};

/**
 * LayoutInspector - Full layout inspector panel
 * NEW: Supports template constraints via templateId prop
 */
const LayoutInspector = ({ 
  layout = {}, 
  background = {},
  cardVariant,
  onLayoutChange,
  onBackgroundChange,
  onCardVariantChange,
  showCardVariant = true,
  templateId = null, // NEW: Template ID for constraint lookup
}) => {
  const [expandedSection, setExpandedSection] = useState('layout');
  
  // Get constraints for this template
  const constraints = useMemo(() => {
    if (!templateId) return null;
    return getTemplateConstraints(templateId);
  }, [templateId]);
  
  const handleLayoutPresetChange = (preset) => {
    onLayoutChange?.({ ...layout, preset });
  };
  
  const handleBackgroundTypeChange = (type) => {
    onBackgroundChange?.({ 
      ...background, 
      type,
      config: type === BackgroundType.NONE ? null : background.config || {},
    });
  };
  
  const handleBackgroundConfigChange = (config) => {
    onBackgroundChange?.({ ...background, config });
  };
  
  const handleOverlayChange = (opacity) => {
    onBackgroundChange?.({ ...background, overlayOpacity: opacity });
  };
  
  return (
    <div className="space-y-4">
      {/* Layout Preset Section */}
      <div className="border-b border-zinc-700 pb-4">
        <button
          onClick={() => setExpandedSection(expandedSection === 'layout' ? null : 'layout')}
          className="w-full flex items-center justify-between text-zinc-200 font-medium text-sm py-2"
        >
          <span className="flex items-center gap-2">
            <Columns className="w-4 h-4 text-orange-400" />
            Layout
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${expandedSection === 'layout' ? 'rotate-180' : ''}`} />
        </button>
        
        {expandedSection === 'layout' && (
          <div className="pt-2">
            <LayoutPresetSelector 
              value={layout.preset || LayoutPreset.SINGLE}
              onChange={handleLayoutPresetChange}
              allowedLayouts={constraints?.allowedLayouts}
            />
          </div>
        )}
      </div>
      
      {/* Background Section */}
      <div className="border-b border-zinc-700 pb-4">
        <button
          onClick={() => setExpandedSection(expandedSection === 'background' ? null : 'background')}
          className="w-full flex items-center justify-between text-zinc-200 font-medium text-sm py-2"
        >
          <span className="flex items-center gap-2">
            <Palette className="w-4 h-4 text-orange-400" />
            Background
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${expandedSection === 'background' ? 'rotate-180' : ''}`} />
        </button>
        
        {expandedSection === 'background' && (
          <div className="pt-2 space-y-4">
            <BackgroundTypeSelector 
              value={background.type}
              onChange={handleBackgroundTypeChange}
              allowedBackgrounds={constraints?.allowedBackgrounds}
            />
            
            {background.type === BackgroundType.SOLID && (
              <SolidBackgroundEditor
                config={background.config}
                onChange={handleBackgroundConfigChange}
              />
            )}
            
            {background.type === BackgroundType.GRADIENT && (
              <GradientBackgroundEditor
                config={background.config}
                onChange={handleBackgroundConfigChange}
              />
            )}
            
            {background.type === BackgroundType.IMAGE && (
              <ImageBackgroundEditor
                config={background.config}
                onChange={handleBackgroundConfigChange}
              />
            )}
            
            {background.type !== BackgroundType.NONE && (
              <OverlayEditor
                value={background.overlayOpacity}
                onChange={handleOverlayChange}
              />
            )}
          </div>
        )}
      </div>

      {/* Card Variant Section (only for sections with cards) */}
      {showCardVariant && onCardVariantChange && (
        <div className="border-b border-zinc-700 pb-4">
          <button
            onClick={() => setExpandedSection(expandedSection === 'card' ? null : 'card')}
            className="w-full flex items-center justify-between text-zinc-200 font-medium text-sm py-2"
          >
            <span className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-orange-400" />
              Card Style
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${expandedSection === 'card' ? 'rotate-180' : ''}`} />
          </button>
          
          {expandedSection === 'card' && (
            <div className="pt-2">
              <CardVariantSelector 
                value={cardVariant || CardVariant.DISCOVERY}
                onChange={onCardVariantChange}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LayoutInspector;
